import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/esm/WarningAmber';
import type { ResourceRollupRow } from '../../services/api/resourcesService';
import type { ResourceStatusBreakdown } from '../../types/procurement';

interface Props {
  rows: ResourceRollupRow[];
  status: ResourceStatusBreakdown[];
  loading: boolean;
}

const fmtNumber = (n: number) =>
  Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—';

const fmtCurrency = (n: number | null) =>
  n === null
    ? '—'
    : n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const resourceTypeLabel = (value: string) => {
  const labels: Record<string, string> = {
    material: 'Material',
    tool: 'Tool',
    equipment: 'Equipment',
    supply: 'Supply',
    labour: 'Labour',
  };
  return labels[value] ?? value;
};

const StatusBar = ({ b, required }: { b: ResourceStatusBreakdown; required: number }) => {
  const total = Math.max(required, b.onSite + b.inTransit + b.ordered + b.notOrdered);
  if (total === 0) return null;
  const seg = (qty: number) => `${(qty / total) * 100}%`;
  return (
    <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', minWidth: 160 }}>
      <Tooltip title={`On-site: ${fmtNumber(b.onSite)}`}>
        <Box sx={{ width: seg(b.onSite), bgcolor: 'success.main' }} />
      </Tooltip>
      <Tooltip title={`In transit: ${fmtNumber(b.inTransit)}`}>
        <Box sx={{ width: seg(b.inTransit), bgcolor: 'info.main' }} />
      </Tooltip>
      <Tooltip title={`Ordered: ${fmtNumber(b.ordered)}`}>
        <Box sx={{ width: seg(b.ordered), bgcolor: 'warning.main' }} />
      </Tooltip>
      <Tooltip title={`Not ordered: ${fmtNumber(b.notOrdered)}`}>
        <Box sx={{ width: seg(b.notOrdered), bgcolor: 'error.main' }} />
      </Tooltip>
    </Box>
  );
};

const isLeadTimeAtRisk = (
  row: ResourceRollupRow,
  status: ResourceStatusBreakdown | undefined
): boolean => {
  if (!status || status.notOrdered <= 0 || !row.earliestNeedDate) return false;
  const daysUntilNeed = Math.ceil(
    (new Date(row.earliestNeedDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  return row.leadTimeDays > daysUntilNeed;
};

export default function ResourceRollupPanel({ rows, status, loading }: Props) {
  const statusById = new Map(status.map((s) => [s.resourceId, s]));

  const totalCost = rows.reduce(
    (sum, r) => (r.totalCost !== null ? sum + r.totalCost : sum),
    0
  );
  const totalGap = rows.reduce((sum, r) => {
    if (!r.procurementEligible) return sum;
    const s = statusById.get(r.resourceId);
    if (!s) return sum;
    const covered = s.onSite + s.inTransit + s.ordered;
    const gap = r.quantityRequired - covered;
    return sum + Math.max(0, gap);
  }, 0);

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Resource roll-up</Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            Procurement gap
          </Typography>
          <Typography
            variant="subtitle1"
            color={totalGap > 0 ? 'error.main' : 'success.main'}
            sx={{ fontWeight: 600 }}
          >
            {fmtNumber(totalGap)} units · {fmtCurrency(totalCost)} committed
          </Typography>
        </Box>
      </Box>
      {loading && <LinearProgress />}
      {!loading && rows.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          No resources required for the tasks in this window.
        </Box>
      )}
      {!loading && rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Resource</TableCell>
                <TableCell align="right">Required</TableCell>
                <TableCell>Status (on-site / transit / ordered / not-ordered)</TableCell>
                <TableCell align="right">Gap</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell>Risk</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const s = statusById.get(row.resourceId);
                const covered = s ? s.onSite + s.inTransit + s.ordered : 0;
                const gap = row.procurementEligible ? Math.max(0, row.quantityRequired - covered) : 0;
                const leadRisk = isLeadTimeAtRisk(row, s);
                return (
                  <TableRow key={row.resourceId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.resourceCode} · lead {row.leadTimeDays}d
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip size="small" variant="outlined" label={resourceTypeLabel(row.resourceType)} />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {fmtNumber(row.quantityRequired)} {row.unitOfMeasure}
                    </TableCell>
                    <TableCell>
                      {!row.procurementEligible ? 'Not procured' : s ? <StatusBar b={s} required={row.quantityRequired} /> : '—'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: gap > 0 ? 'error.main' : 'success.main', fontWeight: 600 }}
                    >
                      {row.procurementEligible ? fmtNumber(gap) : '—'}
                    </TableCell>
                    <TableCell align="right">{fmtCurrency(row.totalCost)}</TableCell>
                    <TableCell>
                      {leadRisk ? (
                        <Chip
                          size="small"
                          color="error"
                          icon={<WarningAmberIcon />}
                          label="Won't arrive in time"
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}
